import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api';
import {
  PlatformView,
  FormValues,
  FileState,
  ServiceResponse,
  ExecuteResult,
  BackendExecution,
} from '@/types/platform';

export const platformService = {
  async getPlatformView(platformId: string): Promise<PlatformView> {
    const response = await apiClient.get(API_ENDPOINTS.PLATFORM.VIEW, {
      params: { platformId },
    });
    return response.data;
  },

  /**
   * Generic config-driven backend action executor (v3+).
   * Builds the request from the BackendExecution descriptor and available state.
   */
  async executeAction(
    execution: BackendExecution,
    context: {
      platformId: string;
      formValues?: FormValues;
      panelFormValues?: FormValues;
      files?: FileState;
      panelFiles?: File[];
      tableRows?: string[][];
    }
  ): Promise<ServiceResponse> {
    const { platformId, formValues = {}, panelFormValues = {}, files = {}, panelFiles = [], tableRows = [] } = context;

    // Resolve query params — substitute $platformId token
    const resolvedParams: Record<string, string> = {};
    if (execution.queryParams) {
      for (const [k, v] of Object.entries(execution.queryParams)) {
        resolvedParams[k] = v === '$platformId' ? platformId : v;
      }
    }

    if (execution.requestType === 'multipart' || execution.files) {
      const formData = new FormData();

      // Build payload: formValues first, then static payload on top so static values always win
      const staticPayload = execution.payload ?? {};
      const payload: Record<string, any> = {};
      if (execution.include?.formData) Object.assign(payload, formValues);
      if (execution.include?.panelFormData) Object.assign(payload, panelFormValues);
      if (execution.include?.tableData) payload.spreadsheetData = tableRows;
      // Static payload applied last — overrides any same-key from formValues
      Object.assign(payload, staticPayload);

      // Apply conditional payload overrides based on form field values
      if (execution.payloadOverrides) {
        for (const override of execution.payloadOverrides) {
          const fieldValue = formValues[override.when.field];
          if (fieldValue === override.when.equals) {
            Object.assign(payload, override.payload);
          }
        }
      }

      // Resolve $token references from formValues and clean up empty values
      for (const key of Object.keys(payload)) {
        const val = payload[key];
        if (typeof val === 'string' && val.startsWith('$')) {
          const refKey = val.slice(1);
          const resolved = formValues[refKey];
          if (resolved === undefined || resolved === null || resolved === '') {
            delete payload[key];
          } else {
            payload[key] = resolved;
          }
        } else if (val === undefined || val === null) {
          delete payload[key];
        }
      }

      const payloadFieldName = execution.multipartParts?.payloadFieldName ?? 'payload';
      formData.append(payloadFieldName, JSON.stringify(payload));

      // Attach files
      if (execution.files) {
        const { source, fieldName } = execution.files;
        const fileList = source === 'panel'
          ? panelFiles
          : Object.values(files).flat();

        if (fileList.length > 0) {
          fileList.forEach(f => formData.append(fieldName, f));
        } else {
          formData.append(fieldName, new Blob([]), '');
        }
      }

      const response = await apiClient.request<ServiceResponse>({
        method: execution.method,
        url: execution.endpoint,
        params: resolvedParams,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return response.data;
    }

    // JSON request — build structured body matching PlatformExportRequest shape
    const staticPayload: Record<string, any> = { ...(execution.payload ?? {}) };
    const payload: Record<string, any> = { ...staticPayload };

    if (execution.include?.formData) {
      payload.formData = { ...formValues };
    }
    if (execution.include?.panelFormData) {
      payload.formData = { ...(payload.formData ?? {}), ...panelFormValues };
    }
    if (execution.include?.tableData) {
      payload.tableData = tableRows;
    }

    // Apply conditional payload overrides based on form field values
    if (execution.payloadOverrides) {
      for (const override of execution.payloadOverrides) {
        const fieldValue = formValues[override.when.field];
        if (fieldValue === override.when.equals) {
          Object.assign(payload, override.payload);
        }
      }
    }

    const response = await apiClient.request<ServiceResponse>({
      method: execution.method,
      url: execution.endpoint,
      params: resolvedParams,
      data: payload,
    });
    return response.data;
  },

  // ─── Legacy methods (v1/v2) ────────────────────────────────────────────────

  async executePlatform(
    platformId: string,
    formValues: FormValues,
    files: FileState,
    processingStrategy: string | null
  ): Promise<ServiceResponse<ExecuteResult>> {
    const formData = new FormData();
    formData.append('platformId', platformId);
    formData.append('payload', JSON.stringify({ ...formValues, processingMode: processingStrategy }));

    const allFiles = Object.values(files).flat();
    if (allFiles.length > 0) {
      allFiles.forEach(file => formData.append('files', file));
    } else {
      formData.append('files', new Blob([]), '');
    }

    const response = await apiClient.post<ServiceResponse>(API_ENDPOINTS.PLATFORM.EXECUTE, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async executePostProcessing(
    platformId: string,
    strategy: string,
    fieldValues: FormValues,
    spreadsheetData: string[][]
  ): Promise<ServiceResponse> {
    const formData = new FormData();
    formData.append('platformId', platformId);
    formData.append(
      'payload',
      JSON.stringify({ ...fieldValues, processingMode: strategy, spreadsheetData })
    );
    formData.append('files', new Blob([]), '');

    const response = await apiClient.post<ServiceResponse>(API_ENDPOINTS.PLATFORM.EXECUTE, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async getCreditBalance(): Promise<number> {
    const response = await apiClient.get<ServiceResponse<number>>(API_ENDPOINTS.PLATFORM.CREDIT_BALANCE);
    return response.data.data;
  },

  async uploadProductList(file: File): Promise<ServiceResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<ServiceResponse>(API_ENDPOINTS.PLATFORM.PRODUCT_LIST, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};
